/**
 * Utility functions for error handling
 */

/**
 * Converts any error-like value to a string representation
 * Handles Error instances, strings, objects, and other types safely
 *
 * @param error - The error to stringify
 * @returns String representation of the error
 *
 * @example
 * ```typescript
 * stringifyError(new Error("Something went wrong")) // "Something went wrong"
 * stringifyError("Simple string error") // "Simple string error"
 * stringifyError({ message: "Object error", code: 500 }) // '{"message":"Object error","code":500}'
 * stringifyError(null) // "null"
 * stringifyError(undefined) // "undefined"
 * ```
 */
export function stringifyError(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === "string") {
        return error;
    }

    if (error && typeof error === "object") {
        try {
            return JSON.stringify(error);
        } catch {
            return "Unserializable error object";
        }
    }

    return String(error);
}

/**
 * Creates an Error instance from any value
 * Useful for ensuring consistent error handling
 *
 * @param error - The error to normalize
 * @returns Error instance
 *
 * @example
 * ```typescript
 * normalizeError("String error") // Error("String error")
 * normalizeError(new Error("Already an error")) // Error("Already an error")
 * normalizeError({ message: "Object error" }) // Error('{"message":"Object error"}')
 * ```
 */
export function normalizeError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    return new Error(stringifyError(error));
}
